async function request(path, options = {}) {
  const headers = new Headers(options.headers || {});
  const locale = window.localStorage.getItem('talent-locale') || 'en';
  if (!headers.has('Accept-Language')) headers.set('Accept-Language', locale);
  let response;
  try {
    response = await fetch(path, { ...options, headers, credentials: 'same-origin' });
  } catch (cause) {
    const error = new Error(`无法连接本地服务，请确认 ${window.location.origin} 正在运行后重试。`);
    error.code = 'service_unreachable';
    error.cause = cause;
    throw error;
  }
  const body = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(body?.error?.message || `请求失败 (${response.status})`);
    error.code = body?.error?.code || 'request_failed';
    error.detail = body?.error?.detail;
    error.status = response.status;
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      window.dispatchEvent(new CustomEvent('talent-auth-required'));
    }
    throw error;
  }
  return body;
}

export const api = {
  authStatus: () => request('/api/auth/status'),
  setupAdmin: (payload) => request('/api/auth/setup', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  login: (payload) => request('/api/auth/login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),
  changePassword: (payload) => request('/api/auth/password', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateProfile: (formData) => request('/api/auth/profile', {
    method: 'PUT', body: formData,
  }),
  state: (jobId) => {
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    return request(`/api/state${params.size ? `?${params}` : ''}`);
  },
  // An explicit locale is needed when the page shows AI narrative alongside a
  // resume translated into a different language than the interface.
  candidate: (id, locale = null) => request(`/api/candidates/${id}`,
    locale ? { headers: { 'Accept-Language': locale } } : {}),
  createJob: (payload) => request('/api/jobs', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateJob: (id, payload) => request(`/api/jobs/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  saveJobForm: (id, payload, file) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(payload)) {
      form.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
    }
    if (file) form.append('jdFile', file);
    return request(id ? `/api/jobs/${id}` : '/api/jobs', { method: id ? 'PUT' : 'POST', body: form });
  },
  deleteJob: (id) => request(`/api/jobs/${id}`, { method: 'DELETE' }),
  formatJobDescription: (text) => request('/api/jobs/format-jd', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }),
  }),
  importCandidates: (formData) => request('/api/import', { method: 'POST', body: formData }),
  assignAsset: (id, payload) => request(`/api/assets/${id}/assign`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  processCandidate: (id) => request(`/api/candidates/${id}/process`, { method: 'POST' }),
  cancelCandidateProcessing: (id) => request(`/api/candidates/${encodeURIComponent(id)}/cancel-processing`, { method: 'POST' }),
  deleteCandidate: (id) => request(`/api/candidates/${id}`, { method: 'DELETE' }),
  deleteCandidates: (candidateIds) => request('/api/candidates/bulk', {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ candidateIds }),
  }),
  transitionCandidate: (id, payload) => request(`/api/candidates/${id}/workflow`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  transitionCandidates: (candidateIds, payload) => request('/api/candidates/bulk/workflow', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, candidateIds }),
  }),
  scheduleInterview: (id, payload) => request(`/api/candidates/${id}/interviews`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  createUser: (payload) => request('/api/users', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateUser: (id, payload) => request(`/api/users/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
  resetUserPassword: (id, payload) => request(`/api/users/${id}/password`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  assignUserDepartments: (id, payload) => request(`/api/users/${id}/departments`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  createDepartment: (payload) => request('/api/departments', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateDepartment: (id, payload) => request(`/api/departments/${id}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  deleteDepartment: (id) => request(`/api/departments/${id}`, { method: 'DELETE' }),
  processAll: (jobId) => request(`/api/jobs/${jobId}/process-all`, { method: 'POST' }),
  retryTask: (id) => request(`/api/tasks/${id}/retry`, { method: 'POST' }),
  cancelTask: (id) => request(`/api/tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
  calibrate: (id, payload) => request(`/api/candidates/${id}/calibrations`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateCalibration: (candidateId, calibrationId, payload) => request(
    `/api/candidates/${candidateId}/calibrations/${calibrationId}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) },
  ),
  deleteCalibration: (candidateId, calibrationId) => request(
    `/api/candidates/${candidateId}/calibrations/${calibrationId}`,
    { method: 'DELETE' },
  ),
  translate: (id, targetLang) => request(`/api/candidates/${id}/translate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ targetLang }),
  }),
  saveSettings: (payload) => request('/api/settings', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  createProvider: (payload) => request('/api/model-providers', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateProvider: (id, payload) => request(`/api/model-providers/${encodeURIComponent(id)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  deleteProvider: (id) => request(`/api/model-providers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  addProviderModel: (providerId, payload) => request(`/api/model-providers/${encodeURIComponent(providerId)}/models`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  updateProviderModel: (providerId, modelId, payload) => request(`/api/model-providers/${encodeURIComponent(providerId)}/models/${encodeURIComponent(modelId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  deleteProviderModel: (providerId, modelId) => request(`/api/model-providers/${encodeURIComponent(providerId)}/models/${encodeURIComponent(modelId)}`, { method: 'DELETE' }),
  refreshProviderModels: (id) => request(`/api/model-providers/${encodeURIComponent(id)}/refresh-models`, { method: 'POST' }),
  testProvider: (id, payload) => request(`/api/model-providers/${encodeURIComponent(id)}/test`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  saveLangfuse: (payload) => request('/api/langfuse/settings', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  testLangfuse: (payload) => request('/api/langfuse/test', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  saveSearchSettings: (payload) => request('/api/search/settings', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  testSearchProvider: () => request('/api/search/test', { method: 'POST' }),
};
